import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Mail, Lock, User, Loader2, ArrowRight, Camera, CheckCircle, Calendar, ShieldCheck, AlertCircle } from 'lucide-react';
import { authAPI } from '../services/api';
import { logout } from '../utils/auth';

const Register = ({ setIsAuthenticated }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  // Step flow: 1 (signup) → 2 (verify code) → 'parent-email' (minors) → 'consent-pending' (minors) → 3 (profile)
  const [formData, setFormData] = useState({
    name: '', username: '', email: '', password: '', confirmPassword: '', signupCode: '', dateOfBirth: ''
  });
  const [parentEmail, setParentEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [profileData, setProfileData] = useState({ persona: '', sport: '', location: '' });
  const [profileImage, setProfileImage] = useState(null);
  const [profilePreview, setProfilePreview] = useState('');
  const [loading, setLoading] = useState(false);
  const [signupCodeError, setSignupCodeError] = useState(false);
  const [errors, setErrors] = useState({});
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [authToken, setAuthToken] = useState('');
  const [isMinor, setIsMinor] = useState(false);
  const [emailExists, setEmailExists] = useState(false);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const profileInputRef = useRef(null);
  const emailCheckTimer = useRef(null);

  useEffect(() => { logout(); }, []);

  const calculateAge = (dob) => {
    if (!dob) return null;
    const birth = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
    return age;
  };

  // Debounced real-time email check
  const checkEmailExists = useCallback((email) => {
    if (emailCheckTimer.current) clearTimeout(emailCheckTimer.current);
    if (!email || !email.includes('@')) {
      setEmailExists(false);
      return;
    }
    emailCheckTimer.current = setTimeout(async () => {
      setCheckingEmail(true);
      try {
        const res = await authAPI.checkEmail(email);
        setEmailExists(res.data.exists);
      } catch {
        setEmailExists(false);
      } finally {
        setCheckingEmail(false);
      }
    }, 500);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: false }));
    if (name === 'email') {
      setEmailExists(false);
      checkEmailExists(value);
    }
  };

  const handleDobChange = (e) => {
    const dob = e.target.value;
    setFormData(prev => ({ ...prev, dateOfBirth: dob }));
    setErrors(prev => ({ ...prev, dateOfBirth: false }));
  };

  // ─── STEP 1: Sign Up (no parent email here) ───
  const handleSignUp = async (e) => {
    e.preventDefault();
    const VALID_SIGNUP_CODE = process.env.REACT_APP_SIGNUP_CODE || 'SPORT1';
    if (formData.signupCode.toUpperCase() !== VALID_SIGNUP_CODE) {
      setSignupCodeError(true);
      return;
    }
    setSignupCodeError(false);

    if (emailExists) {
      toast.error('This email is already registered. Please use a different email.');
      return;
    }

    const newErrors = {};
    ['name', 'username', 'email', 'password', 'confirmPassword', 'dateOfBirth'].forEach(f => { if (!formData[f]) newErrors[f] = true; });

    if (formData.password && formData.confirmPassword && formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = true;
      toast.error('Passwords do not match.');
      setErrors(newErrors);
      return;
    }

    const age = calculateAge(formData.dateOfBirth);
    if (age !== null && age < 13) {
      newErrors.dateOfBirth = true;
      toast.error('You must be at least 13 years old to register.');
      setErrors(newErrors);
      return;
    }
    if (Object.keys(newErrors).length > 0) { setErrors(newErrors); return; }

    setLoading(true);
    try {
      const data = new FormData();
      Object.entries(formData).forEach(([k, v]) => { if (k !== 'confirmPassword') data.append(k, v); });
      const res = await authAPI.register(data);
      setRegisteredEmail(res.data.email || formData.email);
      setIsMinor(!!res.data.isMinor);
      setStep(2);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ─── STEP 2: Verify Code ───
  const handleVerifyCode = async (e) => {
    e.preventDefault();
    if (verificationCode.length !== 6) { toast.error('Please enter the 6-digit code'); return; }

    setLoading(true);
    try {
      const res = await authAPI.verifyCode(registeredEmail, verificationCode);
      const { access_token } = res.data;

      if (isMinor) {
        // Minor verified → go to parent email step
        setStep('parent-email');
      } else {
        setAuthToken(access_token);
        localStorage.setItem('token', access_token);
        setStep(3);
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Invalid code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    try {
      await authAPI.resendCode(registeredEmail);
      toast.success('New code sent to your email');
    } catch {
      toast.error('Failed to resend code');
    }
  };

  // ─── STEP 'parent-email': Submit parent email (minors only) ───
  const handleSubmitParentEmail = async (e) => {
    e.preventDefault();
    if (!parentEmail || !parentEmail.includes('@')) {
      setErrors(prev => ({ ...prev, parentEmail: true }));
      toast.error('Please enter a valid parent/guardian email.');
      return;
    }
    if (parentEmail.trim().toLowerCase() === registeredEmail.trim().toLowerCase()) {
      setErrors(prev => ({ ...prev, parentEmail: true }));
      toast.error('Parent email must be different from your own email.');
      return;
    }

    setLoading(true);
    try {
      await authAPI.setParentEmail(registeredEmail, parentEmail.trim());
      toast.success('Consent email sent to your parent/guardian!');
      setStep('consent-pending');
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to send consent email.');
    } finally {
      setLoading(false);
    }
  };

  // ─── STEP 3: Complete Profile ───
  const handleCompleteProfile = async (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!profileData.persona) newErrors.persona = true;
    if (!profileData.sport) newErrors.sport = true;
    if (!profileData.location) newErrors.location = true;
    if (Object.keys(newErrors).length > 0) { setErrors(newErrors); toast.error('Please fill all required fields'); return; }

    setLoading(true);
    try {
      const data = new FormData();
      data.append('persona', profileData.persona);
      data.append('sports', JSON.stringify([profileData.sport]));
      data.append('location', profileData.location);
      if (profileImage instanceof File) data.append('profileImage', profileImage, profileImage.name);
      const res = await authAPI.completeProfile(data);
      const { access_token, user } = res.data;
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(user));
      localStorage.setItem('userId', user._id);
      setIsAuthenticated(true);
      navigate('/home');
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to save profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ─── Step Indicator ───
  const StepIndicator = () => {
    const steps = isMinor ? [1, 2, 'parent-email', 'consent-pending'] : [1, 2, 3];
    const currentIdx = steps.indexOf(step);
    return (
      <div className="flex justify-center gap-2 mt-4">
        {steps.map((s, i) => (
          <div key={i} className={`h-2 w-12 rounded-full transition-colors ${i <= currentIdx ? 'bg-primary' : 'bg-muted'}`} />
        ))}
      </div>
    );
  };

  // ─── STEP 1 UI ───
  if (step === 1) {
    const age = calculateAge(formData.dateOfBirth);
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-background">
        <Card className="w-full max-w-md shadow-lg">
          <CardHeader className="space-y-3 text-center">
            <div className="flex justify-center mb-4">
              <div className="bg-white rounded-full p-4 shadow-md">
                <img src="/logo.png" alt="SportiZn" className="h-14 w-auto" />
              </div>
            </div>
            <CardTitle className="text-2xl font-bold">Join the Future of Sport</CardTitle>
            <CardDescription>Create Your Sports Account</CardDescription>
            <StepIndicator />
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSignUp} className="space-y-4">
              <div className="space-y-2">
                <Label>Full Name *</Label>
                <div className="relative">
                  <User className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input name="name" placeholder="William Johnson" value={formData.name} onChange={handleChange} className={`pl-10 ${errors.name ? 'border-red-500 border-2' : ''}`} />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Username *</Label>
                <div className="relative">
                  <User className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input name="username" placeholder="William_J" value={formData.username} onChange={handleChange} className={`pl-10 ${errors.username ? 'border-red-500 border-2' : ''}`} />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Email *</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input name="email" type="email" placeholder="your@email.com" value={formData.email} onChange={handleChange} className={`pl-10 ${errors.email || emailExists ? 'border-red-500 border-2' : ''}`} data-testid="email-input" />
                  {checkingEmail && <Loader2 className="absolute right-3 top-3 h-4 w-4 animate-spin text-muted-foreground" />}
                </div>
                {emailExists && (
                  <div className="flex items-center gap-1.5 text-destructive">
                    <AlertCircle className="w-3.5 h-3.5" />
                    <p className="text-xs font-medium">This email is already registered. Please try another email.</p>
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label>Signup Code *</Label>
                <Input
                  name="signupCode" placeholder="Enter 6-digit code" maxLength={6}
                  value={formData.signupCode}
                  onChange={(e) => { setFormData(prev => ({...prev, signupCode: e.target.value.toUpperCase()})); setSignupCodeError(false); }}
                  className={`uppercase tracking-wider ${signupCodeError ? 'border-red-500 border-2' : ''}`}
                />
                {signupCodeError && <p className="text-xs text-destructive font-medium">Wrong code</p>}
              </div>
              <div className="space-y-2">
                <Label>Password *</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input name="password" type="password" placeholder="Create a strong password" value={formData.password} onChange={handleChange} className={`pl-10 ${errors.password ? 'border-red-500 border-2' : ''}`} />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Confirm Password *</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input name="confirmPassword" type="password" placeholder="Re-enter your password" value={formData.confirmPassword} onChange={handleChange} className={`pl-10 ${errors.confirmPassword ? 'border-red-500 border-2' : ''}`} data-testid="confirm-password-input" />
                </div>
                {formData.password && formData.confirmPassword && formData.password !== formData.confirmPassword && (
                  <p className="text-xs text-destructive font-medium">Passwords do not match.</p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Date of Birth *</Label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    name="dateOfBirth" type="date"
                    value={formData.dateOfBirth}
                    onChange={handleDobChange}
                    max={new Date().toISOString().split('T')[0]}
                    className={`pl-10 ${errors.dateOfBirth ? 'border-red-500 border-2' : ''}`}
                    data-testid="dob-input"
                  />
                </div>
                {age !== null && age < 13 && (
                  <p className="text-xs text-destructive font-medium">You must be at least 13 years old to register.</p>
                )}
                {age !== null && age >= 13 && age < 18 && (
                  <div className="flex items-center gap-2 text-amber-500 bg-amber-500/10 rounded-lg p-3 mt-1">
                    <ShieldCheck className="w-4 h-4 flex-shrink-0" />
                    <p className="text-xs">Since you're under 18, parental consent will be required after email verification.</p>
                  </div>
                )}
              </div>
              <Button type="submit" className="w-full text-white bg-brand-gradient" disabled={loading || emailExists} data-testid="signup-btn">
                {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Signing up...</> : <>Sign Up <ArrowRight className="w-4 h-4 ml-2" /></>}
              </Button>
            </form>
            <p className="text-center text-sm text-gray-600 mt-6">
              Already have an account? <Link to="/login" className="font-semibold hover:underline text-primary">Sign in</Link>
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ─── STEP 2 UI: Verification Code ───
  if (step === 2) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-background">
        <Card className="w-full max-w-md shadow-lg">
          <CardHeader className="space-y-3 text-center">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center">
                <Mail className="w-8 h-8 text-primary" />
              </div>
            </div>
            <CardTitle className="text-2xl font-bold">Verify Your Email</CardTitle>
            <CardDescription className="text-base">
              Enter the 6-digit code sent to <strong className="text-foreground">{registeredEmail}</strong>
            </CardDescription>
            <StepIndicator />
          </CardHeader>
          <CardContent>
            <form onSubmit={handleVerifyCode} className="space-y-4">
              <Input
                type="text" inputMode="numeric" maxLength={6} placeholder="000000"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="text-center text-3xl tracking-[0.5em] font-mono h-14"
                data-testid="verification-code-input"
                autoFocus
              />
              <Button type="submit" className="w-full text-white bg-brand-gradient" disabled={loading || verificationCode.length !== 6} data-testid="verify-code-btn">
                {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Verifying...</> : <>Verify <CheckCircle className="w-4 h-4 ml-2" /></>}
              </Button>
              <Button type="button" variant="ghost" className="w-full text-muted-foreground" onClick={handleResendCode} data-testid="resend-code-btn">
                Didn't receive code? Resend
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ─── PARENT EMAIL STEP (Minors only — after verification) ───
  if (step === 'parent-email') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-background">
        <Card className="w-full max-w-md shadow-lg">
          <CardHeader className="space-y-3 text-center">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 rounded-full bg-amber-500/20 flex items-center justify-center">
                <ShieldCheck className="w-8 h-8 text-amber-500" />
              </div>
            </div>
            <CardTitle className="text-2xl font-bold">Parental Consent</CardTitle>
            <CardDescription className="text-base">
              Since you're under 18, a parent or guardian must approve your account. Enter their email below — we'll send them an approval link.
            </CardDescription>
            <StepIndicator />
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmitParentEmail} className="space-y-4">
              <div className="space-y-2">
                <Label>Parent/Guardian Email *</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    type="email"
                    placeholder="parent@email.com"
                    value={parentEmail}
                    onChange={(e) => { setParentEmail(e.target.value); setErrors(prev => ({ ...prev, parentEmail: false })); }}
                    className={`pl-10 ${errors.parentEmail ? 'border-red-500 border-2' : ''}`}
                    data-testid="parent-email-input"
                    autoFocus
                  />
                </div>
              </div>
              <div className="bg-muted/50 rounded-lg p-3">
                <p className="text-xs text-muted-foreground">Your parent/guardian will receive an email with a link to approve your account. You won't be able to log in until they approve.</p>
              </div>
              <Button type="submit" className="w-full text-white bg-brand-gradient" disabled={loading} data-testid="submit-parent-email-btn">
                {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</> : <>Send Consent Request <ArrowRight className="w-4 h-4 ml-2" /></>}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ─── CONSENT PENDING UI (Minors — after parent email submitted) ───
  if (step === 'consent-pending') {
    const handleResendConsent = async () => {
      try {
        await authAPI.resendParentalConsent(registeredEmail);
        toast.success('Consent email resent to your parent/guardian');
      } catch {
        toast.error('Failed to resend consent email');
      }
    };
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-background">
        <Card className="w-full max-w-md shadow-lg">
          <CardHeader className="space-y-3 text-center">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 rounded-full bg-amber-500/20 flex items-center justify-center">
                <ShieldCheck className="w-8 h-8 text-amber-500" />
              </div>
            </div>
            <CardTitle className="text-2xl font-bold">Waiting for Approval</CardTitle>
            <CardDescription className="text-base">
              We've sent an approval request to <strong className="text-foreground">{parentEmail}</strong>. Once your parent/guardian approves, you can log in.
            </CardDescription>
            <StepIndicator />
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4 text-center">
              <p className="text-sm text-foreground">Your account is verified and ready — just waiting for parental approval.</p>
            </div>
            <Button variant="outline" className="w-full" onClick={handleResendConsent} data-testid="resend-consent-btn">
              Resend Consent Email
            </Button>
            <Button variant="ghost" className="w-full text-muted-foreground" onClick={() => navigate('/login')} data-testid="go-to-login-btn">
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ─── STEP 3 UI: Complete Profile ───
  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-background">
      <Card className="w-full max-w-md shadow-lg">
        <CardHeader className="space-y-3 text-center">
          <CardTitle className="text-2xl font-bold">Set Up Your Profile</CardTitle>
          <CardDescription>Add your details to get started</CardDescription>
          <StepIndicator />
        </CardHeader>
        <CardContent>
          <form onSubmit={handleCompleteProfile} className="space-y-4">
            <div className="flex justify-center mb-4">
              <div
                className="w-24 h-24 rounded-full bg-muted border-2 border-dashed border-muted-foreground/30 flex items-center justify-center cursor-pointer overflow-hidden hover:border-primary/50 transition-colors relative"
                onClick={(e) => { e.stopPropagation(); profileInputRef.current?.click(); }}
                data-testid="profile-image-upload"
              >
                {profilePreview ? (
                  <img src={profilePreview} alt="Profile" className="w-full h-full object-cover" />
                ) : (
                  <div className="flex flex-col items-center text-muted-foreground">
                    <Camera className="w-6 h-6 mb-1" />
                    <span className="text-xs">Add Photo</span>
                  </div>
                )}
              </div>
              <input
                ref={profileInputRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp"
                className="hidden" data-testid="profile-image-input"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) { setProfileImage(f); setProfilePreview(URL.createObjectURL(f)); } }}
              />
            </div>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Profile Type *</Label>
                <Select onValueChange={(v) => { setProfileData({...profileData, persona: v}); setErrors({...errors, persona: false}); }} value={profileData.persona}>
                  <SelectTrigger className={errors.persona ? 'border-red-500 border-2' : ''} data-testid="persona-select"><SelectValue placeholder="Select your profile type" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="athlete">Athlete</SelectItem>
                    <SelectItem value="coach">Coach</SelectItem>
                    <SelectItem value="club">Club/Team</SelectItem>
                    <SelectItem value="nutritionist">Nutritionist</SelectItem>
                    <SelectItem value="physio">Physiotherapist</SelectItem>
                    <SelectItem value="fan">Fan/Enthusiast</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Sports Category *</Label>
                <Select onValueChange={(v) => { setProfileData({...profileData, sport: v}); setErrors({...errors, sport: false}); }} value={profileData.sport}>
                  <SelectTrigger className={errors.sport ? 'border-red-500 border-2' : ''} data-testid="sport-select"><SelectValue placeholder="Select your sport" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Football">Football/Soccer</SelectItem>
                    <SelectItem value="Basketball">Basketball</SelectItem>
                    <SelectItem value="Tennis">Tennis</SelectItem>
                    <SelectItem value="Swimming">Swimming</SelectItem>
                    <SelectItem value="Track & Field">Track & Field</SelectItem>
                    <SelectItem value="Baseball">Baseball</SelectItem>
                    <SelectItem value="Volleyball">Volleyball</SelectItem>
                    <SelectItem value="Golf">Golf</SelectItem>
                    <SelectItem value="Boxing">Boxing/MMA</SelectItem>
                    <SelectItem value="Cycling">Cycling</SelectItem>
                    <SelectItem value="Gymnastics">Gymnastics</SelectItem>
                    <SelectItem value="Hockey">Hockey</SelectItem>
                    <SelectItem value="Rugby">Rugby</SelectItem>
                    <SelectItem value="Martial Arts">Martial Arts</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Location *</Label>
                <Input placeholder="London, UK" value={profileData.location} onChange={(e) => { setProfileData({...profileData, location: e.target.value}); setErrors({...errors, location: false}); }} className={errors.location ? 'border-red-500 border-2' : ''} data-testid="location-input" />
              </div>
            </div>
            <Button type="submit" className="w-full text-white bg-brand-gradient" disabled={loading} data-testid="finish-profile-btn">
              {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : 'Finish'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default Register;
